/* eslint-disable @typescript-eslint/naming-convention */
/**
 * Define here models used
 * with the backend server for auth
 **/

import { Attachment } from './attachment.models';
import { CustomerSupplier } from './customer-supplier.model';
import {
  Organization,
  OrganizationUser,
  OrganizationUserInfo
} from './organization.models';
import { Roles } from './roles.models';

export type User = {
  id: string;
  username: string;
  roles: Array<UserRole>;
  role: Partial<Role>;
  enabled: boolean;
  locked: boolean;
  passwordChanged: boolean;
  propertiesVerified: number;
  accountExpiresAt: string;
  credentialsExpiresAt: string;
  enabledUpdatedAt: string;
  passwordUpdatedAt?: string;
  email?: string;
  phoneNumber?: string;
  firstName?: string;
  lastName?: string;
  gender?: 'female' | 'male';
  birthdate?: string;
  countryCode?: string;
  addresses?: string[];
  postBox?: string;
  preference?: {
    'security.authentication.two_factor': boolean;
    'security.authentication.watchdog': boolean;
    'security.authentication.black_list.ip_address': string[];
  };
  avatar?: Attachment;
  createdAt: string;
  updatedAt: string;
  lastLogin: string;
  city?: string;
  job?: string;
  taxCenters?: string[];
  accountingOffices?: string[];
  matricule?: string;
  securityChallenge: {
    type: 'none' | '2fa';
  };
  // computed properties
  emailVerified: boolean;
  phoneNumberVerified: boolean;
  fullName?: string;
  organization: Organization;
  supplierInfos?: CustomerSupplier;
  userOrganizations?: OrganizationUserInfo[];
  scopes?: Roles[];
  organizationScopes?: OrganizationUser[];
};

type Role = {
  id: string;
  name: string;
  username: string;
  organization: Organization;
  permissions: Permissions;
  roles: UserRole[];
  updatedAt: string;
};

export enum UserVerifiedStatus {
  email = 1,
  phoneNumber = 2
}

export enum UserRole {
  ROLE_USER = 'ROLE_USER',
  ROLE_SUPER_ADMIN = 'ROLE_SUPER_ADMIN',
  ROLE_ADMIN = 'ROLE_ADMIN',
  ROLE_ORGANIZATION_ADMIN = 'ROLE_ORGANIZATION_ADMIN',
  ROLE_ORGANIZATION = 'ROLE_ORGANIZATION'
}

export type UserCreationParams = {
  email: string;
  phoneNumber?: string;
  password?: string;
  redirectUrl?: string;
  firstName?: string;
  lastName?: string;
  countryCode?: string;
  city?: string;
  district?: string;
  addresses?: string[];
  postBox?: string;
  locked?: boolean;
  isAdmin?: boolean;
  accountExpiresAt?: string;
  credentialsExpiresAt?: string;
  organization?: OrganizationInitParams;
};

export type OrganizationInitParams = {
  name?: string;
  nif?: string;
  ntd?: string;
  taxSystem?: string;
  taxCenter?: string;
  activitySector?: string;
  taxDirection?: string;
  activity?: string;
  accountantRoleId?: string;
  statusLabel?: string;
};

export type ChangePasswordParams = {
  password: string;
  newPassword: string;
};

export type AuthResponse = {
  method: 'password' | 'otp' | 'accessToken' | 'refreshToken';
  user: User;
  tokens: TokensResponse | OtpResponse;
  challenge?: Challenge;
};

export type TokensResponse = {
  accessToken: AccessToken;
  refreshToken: RefreshToken;
};

export type Otp = {
  type: 'totp';
  context?: string;
  issuedAt: string;
  expiresAt: string;
};

export type OtpResponse = {
  otp: Otp;
};

export type AccessToken = {
  id: string;
  issuedAt: string;
  expiresAt: string;
  device: {
    id: string;
    userAgent: string;
  };
};

export type RefreshToken = {
  id: string;
  issuedAt: string;
};

export type LoginParams = {
  username: string;
  password: string;
  redirectUrl?: string;
  accessTokenId?: string;
  organizationIdToConnectTo?: string;
  keepSession?: boolean;
};

export type Challenge = {
  type: '2fa' | 'watchdog';
  field: string;
  value: string;
};

export enum SocialNetworkName {
  google = 'google',
  facebook = 'facebook',
  apple = 'apple'
}

export type SocialLoginParams = {
  name: SocialNetworkName;
  tokenId: string;
  isRegistration?: boolean;
};

export type RegistrationParams = {
  fullName?: string;
  email?: string;
  password?: string;
  phoneNumber?: string;
};

export type RequestPasswordResetParams = {
  email: string;
};

export type ResetPasswordParams = {
  token: string;
  password: string;
};
